import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, TrendingUp, Wallet, Home, KeyRound, Percent } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell,
  AreaChart, Area, CartesianGrid,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("cm_token") || ""}` });
const COLORS = ["#0A192F", "#10B981", "#64748B", "#94A3B8", "#34D399", "#CBD5E1", "#1E3A5F", "#059669"];

const Card = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-[0_10px_30px_rgba(10,25,47,0.04)]" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">{label}</p>
      <span className="w-9 h-9 rounded-xl bg-[#0A192F] flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#10B981]" />
      </span>
    </div>
    <p className="font-display text-3xl font-extrabold text-[#0A192F]">{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

const Panel = ({ title, children, testid }) => (
  <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-[0_10px_30px_rgba(10,25,47,0.04)]" data-testid={testid}>
    <h3 className="font-display font-bold text-[#0A192F] mb-5">{title}</h3>
    {children}
  </div>
);

export const StatsTab = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/stats`, { headers: authHeaders(), withCredentials: true })
      .then(({ data }) => setStats(data))
      .catch(() => setStats(null));
  }, []);

  if (!stats) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-[#10B981]" />
      </div>
    );
  }

  const t = stats.totali;
  return (
    <div className="space-y-6" data-testid="admin-stats">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <Card icon={Home} label="Richieste" value={t.richieste} sub="totali attive" testid="stats-total-requests" />
        <Card icon={KeyRound} label="Sblocchi venduti" value={t.sblocchi_venduti} sub="contatti sbloccati" testid="stats-unlocks" />
        <Card icon={TrendingUp} label="Canoni attivi" value={t.canoni_attivi} sub="abbonamenti (storico)" testid="stats-subs" />
        <Card icon={Wallet} label="Fatturato" value={`€ ${t.fatturato_eur.toLocaleString("it-IT")}`} sub="sblocchi + canoni" testid="stats-revenue" />
        <Card icon={Percent} label="Tasso sblocco" value={`${t.tasso_sblocco}%`} sub="richieste sbloccate" testid="stats-conversion" />
        <Card
          icon={Percent}
          label="Riscontri privati"
          value={stats.riscontri ? `${stats.riscontri.contattati_pct}%` : "—"}
          sub={stats.riscontri ? `contattati · ${stats.riscontri.appuntamenti_pct}% appuntamenti · ${stats.riscontri.totali} risposte` : "nessuna risposta ancora"}
          testid="stats-feedback"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Domanda per categoria" testid="stats-by-category">
          {stats.categorie.length === 0 ? (
            <p className="text-sm text-slate-400">Nessun dato ancora.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.categorie} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="categoria" tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: "rgba(16,185,129,0.06)" }} />
                <Bar dataKey="richieste" fill="#10B981" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
          {stats.categorie.length > 0 && (
            <div className="mt-4 space-y-2">
              {stats.categorie.map((c) => (
                <div key={c.categoria} className="flex justify-between text-xs text-slate-500 border-t border-slate-100 pt-2">
                  <span className="font-semibold text-[#0A192F]">{c.categoria}</span>
                  <span>
                    budget medio € {c.budget_medio.toLocaleString("it-IT")} · max € {c.budget_max.toLocaleString("it-IT")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Province più cercate" testid="stats-top-provinces">
          {stats.top_province.length === 0 ? (
            <p className="text-sm text-slate-400">Nessun dato ancora.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={stats.top_province} dataKey="richieste" nameKey="nome" innerRadius={60} outerRadius={95} paddingAngle={3}>
                  {stats.top_province.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {stats.top_province.map((p, i) => (
              <span key={p.nome} className="text-[11px] font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1">
                <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: COLORS[i % COLORS.length] }} />
                {p.nome} ({p.richieste})
              </span>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title="Richieste negli ultimi 30 giorni" testid="stats-per-day">
        {stats.per_giorno.length === 0 ? (
          <p className="text-sm text-slate-400">Nessun dato ancora.</p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={stats.per_giorno} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <defs>
                <linearGradient id="gradReq" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="data" tick={{ fontSize: 10, fill: "#94A3B8" }} tickLine={false} axisLine={false} tickFormatter={(d) => d.slice(5)} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} />
              <Tooltip />
              <Area type="monotone" dataKey="richieste" stroke="#10B981" strokeWidth={2.5} fill="url(#gradReq)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Panel>

      <Panel title="Comuni più richiesti" testid="stats-top-towns">
        {stats.top_comuni.length === 0 ? (
          <p className="text-sm text-slate-400">Nessun dato ancora.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {stats.top_comuni.map((c, i) => (
              <div key={c.nome} className="rounded-2xl bg-slate-50 border border-slate-100 px-4 py-3">
                <p className="font-display font-bold text-[#0A192F] text-sm">{c.nome}</p>
                <p className="text-xs text-slate-400 mt-0.5">{c.richieste} richieste · #{i + 1}</p>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};
